import { Message, RichBlockMessage } from '@botpress/webchat'
import { Link, useSearch } from '@tanstack/react-router'
import { ComponentProps } from 'react'
import { useFeature } from '~/hooks'
import { cn } from '~/utils'
import { MessageFeedbackBadge } from '../MessageFeedbackBadge'
import { MessageTimestampTooltip } from '../MessageTimestampTooltip'

type MessageProps = ComponentProps<typeof Message> &
  RichBlockMessage & {
    id: string
    workspaceId?: string
    botId?: string
    conversationId?: string
    selected?: boolean
    disableLink?: boolean
    botAvatar?: string
    botName?: string
  }

export const MessageItem = ({
  ref,
  direction,
  block,
  disableInput,
  children,
  sender,
  id,
  selected,
  disableLink,
  timestamp,
  metadata,
  workspaceId,
  botId,
  conversationId,
}: MessageProps) => {
  const search = useSearch({ strict: false })
  const feedbackEnabled = useFeature('dashboard-conv-feedback')
  const feedbackValue = metadata?.['webchat:feedbackValue'] as 'positive' | 'negative' | undefined
  const feedbackComment = metadata?.['webchat:feedbackComment'] as string | undefined

  const link = (
    <Link
      resetScroll={false}
      preload={false}
      to="/workspaces/$workspaceId/bots/$botId/conversations/$conversationId/messages/$messageId"
      params={{
        workspaceId: workspaceId ?? '',
        botId: botId ?? '',
        conversationId: conversationId ?? '',
        messageId: id,
      }}
      search={search}
      disabled={disableLink || !(workspaceId && botId && conversationId)}
    >
      {block && (
        <Message
          id={id}
          direction={direction || 'incoming'}
          timestamp={timestamp}
          block={block}
          sender={sender}
          isReadOnly={true}
        />
      )}
    </Link>
  )

  return (
    <div
      className={cn(
        selected &&
          '[&_.bpMessageBlocksBubble]:ring-2 [&_.bpMessageBlocksBubble]:ring-primary-700 [&_.bpMessageBlocksBubble]:ring-offset-2 [&_.bpMessageBlocksBubble]:ring-offset-neutral-50'
      )}
      data-disable-input={!!disableInput}
      data-direction={direction}
      data-selected={selected}
      dir="auto"
      ref={ref}
    >
      {feedbackEnabled && timestamp ? (
        <MessageTimestampTooltip timestamp={timestamp}>{link}</MessageTimestampTooltip>
      ) : (
        link
      )}
      {feedbackEnabled && feedbackValue && (
        <MessageFeedbackBadge feedbackValue={feedbackValue} feedbackComment={feedbackComment} />
      )}
      {children}
    </div>
  )
}

MessageItem.displayName = 'MessageItem'
